import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { OrderService } from 'src/app/order.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from 'src/app/models/store';
import { CardDetails, cardBrandIcon } from 'src/app/models/order';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { PaymentMethodsBottomSheetComponent } from 'src/app/order-summary/payment-methods-bottom-sheet/payment-methods-bottom-sheet-component';
import { PaymentOptionService, PaymentOption } from 'src/app/_services/payment-option.service';
import { Constants } from 'src/app/constants';
import { AnalyticsService } from 'src/app/_services/analytics.service';
import {WalletComponent} from '../../order-summary/wallet/wallet.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DEVICE_TOKEN_KEY} from 'src/app/_helpers/auth.interceptor';
import { Subject, Subscription, interval, EMPTY } from 'rxjs';
import { takeWhile, timeout, catchError } from 'rxjs/operators';
import { throwError, TimeoutError } from 'rxjs';

@Component({
  selector: 'app-offer-summary',
  templateUrl: './offer-summary.component.html',
  styleUrls: ['./offer-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferSummaryComponent implements OnInit, OnDestroy {
  /** Brand icon for a saved card; falls back to a generic card when the gateway's `card_type` has no artwork. */
  readonly cardBrandIcon = cardBrandIcon;

  private paymentOptionSub: Subscription;
  storeId;
  offerOrderId;
  offersInCart;
  store:Store;
  formQuantity = Array.from(Array(1).keys()).map(n => n+1);
  displayedColumns: string[] = ['quantity', 'item', 'itemTotal'];
  isLoading = true;
  phoneNumber
  otpSent
  otpSending = false;
  otp
  preferredCard
  guestCardDetails:CardDetails[];
  paymentGateway;
  paymentOption:PaymentOption;
  isSubmitting = false;  // ENG-4505: prevent double-submit on gift card purchase
  isPaymentProcessing = false;  // ENG-5090: payment in progress (polling state)
  private pollingActive = false;
  giftDetails: any = null;  // ENG-5034: gift recipient details captured on the offer page

  constructor(
    private route: ActivatedRoute,
    private bottomSheet: MatBottomSheet,
    private router: Router,
    private orderService:OrderService,
    private paymentOptionService:PaymentOptionService,
    private ref: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar) { }

  ngOnInit() {
    // ENG-5669: back from saving a Worldnet card for this purchase (payment bottom sheet); the
    // flag is for the order summary's card verification, not for a gift card.
    try { localStorage.removeItem('pending_worldnet_card_added'); } catch (e) { /* storage blocked */ }
    this.storeId = this.route.snapshot.paramMap.get('storeId');
    this.offerOrderId = this.route.snapshot.paramMap.get('offerOrderId');

    this.paymentOptionSub = this.paymentOptionService.current.subscribe(paymentOption =>{
      if(paymentOption){
        this.paymentOption = paymentOption;
        this.loadOffer();
        this.ref.markForCheck();
      }
    })

    this.loadOffer();

    // ENG-5316: Restore polling state if user refreshed during payment processing
    this.restorePaymentProcessingState();
  }

  private restorePaymentProcessingState() {
    if (localStorage.getItem(`offer_payment_processing_${this.offerOrderId}`)) {
      this.isPaymentProcessing = true;
      this.ref.markForCheck();
      this.startPaymentPolling();
    }
  }

  private savePaymentProcessingState() {
    localStorage.setItem(`offer_payment_processing_${this.offerOrderId}`, 'true');
  }

  private clearPaymentProcessingState() {
    localStorage.removeItem(`offer_payment_processing_${this.offerOrderId}`);
  }

  private loadGiftDetails() {
    const saved = localStorage.getItem(`giftDetails_${this.offerOrderId}`);
    if (saved) {
      try {
        this.giftDetails = JSON.parse(saved);
      } catch (e) {
        this.giftDetails = null;
      }
    } else {
      this.giftDetails = null;
    }
  }

  removeGift() {
    localStorage.removeItem(`giftDetails_${this.offerOrderId}`);
    this.giftDetails = null;
    this.ref.markForCheck();
  }

  editGift() {
    const firstItem = this.offersInCart?.selected_offers?.[0];
    if (firstItem) {
      this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'offer', firstItem.offer.id]);
    }
  }

  private loadOffer() {
    this.isLoading = true;
    this.loadGiftDetails();
    this.orderService.getOffer(this.storeId, this.offerOrderId)
    .subscribe(offersInCart => {
      if(offersInCart.offer_order.is_submitted){
        // ENG-5090: if payment is still in progress, poll instead of navigating to success
        if (offersInCart.offer_order.payment_status === 'IN_PROGRESS') {
          this.isPaymentProcessing = true;
          this.startPaymentPolling();
        } else {
          this.router.navigate(['store', this.storeId,'offer', 'order', this.offerOrderId, 'status', 'success']);
        }
      }
      this.offersInCart = offersInCart;
      this.paymentGateway = offersInCart.store.payment_gateway;

      if(this.offersInCart.offer_order.phone_number){
        this.phoneNumber = this.getFormattedPhoneNumber(this.offersInCart.offer_order.phone_number);
      }
      this.store = Object.assign(new Store(), offersInCart.store);
      this.analyticsService.setStore(this.getStoreId(), this.store.store_name);
      if(this.offersInCart.selected_offers.length <= 0){
        this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'available'])
      }
      this.orderService.getPreferredPaymentForOffer(this.getStoreId(), this.offerOrderId, this.getPlainPhoneNumber(), this.paymentGateway).subscribe(data => {
        this.preferredCard = Object.assign(new CardDetails(), data['guest_payment_detail'])
        this.isLoading = false;
        this.ref.markForCheck();
      },
      error => {
        this.isLoading = false;
        this.ref.markForCheck();
      });
      this.ref.markForCheck();
    })
  }

  getPlainPhoneNumber(){
    if(this.phoneNumber){
      let ph = this.phoneNumber.replace(/[\s()-]/g, '')
      return '+1' + ph.substring(0,10)
    }
    return "";
  }

  getPlainPhoneNumberFromString(phoneNumber:string){
    let ph = phoneNumber.replace(/[\s()-]/g, '')
    return '+1' + ph.substring(0,10)
  }

  getFormattedPhoneNumber(phoneNumber){
    return phoneNumber.replace(/^(\+1)(\d{0,3})(\d{0,3})(\d{0,4})/, '($2) $3-$4')
  }

  updateQuantity(item){
    if(item.quantity <= 0){
      this.deleteItem(item);
    }else{
      this.orderService.updateOfferQuantity(this.storeId, this.offerOrderId, item.id, item.quantity).subscribe(data => {
        this.loadOffer();
      })
    }
  }

  deleteItem(item){
    this.orderService.deleteOffer(this.storeId, this.offerOrderId, item.id).subscribe(data =>{
      this.loadOffer();
    })
  }

  onSelect(item){
    // parsed_item_id = item.id
    this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'offer', item.offer.id]);
  }

  goBack(){
    this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'available']);
  }

  sendOTP(){
    if(this.otpSending) return;
    this.otpSending = true;
    this.orderService.otpForOffer(this.storeId, this.offerOrderId, this.getPlainPhoneNumber()).subscribe(() => {
      this.otpSent = true;
      this.otpSending = false;
      this.ref.markForCheck();
    }, err => {
      this.otpSending = false;
      // The backend caps sends per number and per IP and answers 429 with a reason. Show it
      // instead of failing silently, as the food checkout does.
      const message = err?.error?.error || 'Could not send the code. Please try again.';
      this.snackBar.open(message, 'OK', { duration: 4000, verticalPosition: 'top' });
      this.ref.markForCheck();
    })
  }

  verifyOTP(){
    this.orderService.verifyOtpForOffer(this.storeId, this.offerOrderId, this.getPlainPhoneNumber(), this.otp).subscribe(data => {
      // A wrong code comes back as 200 with an empty body, not as an error. Treat it as a
      // failure here, or the page carries on with phone_number_verified = undefined.
      if (!data || !data['is_verified']) {
        this.snackBar.open('That code is incorrect. Please check it and try again.', 'OK',
          { duration: 4000, verticalPosition: 'top' });
        this.ref.markForCheck();
        return;
      }
      localStorage.setItem(Constants.PHONE_NUMBER_KEY, this.getPlainPhoneNumber())
      // The server issues this once the number is proved; the interceptor sends it back as
      // X-Device-Token and store-header shows My Account / Wallet / Gift Cards only when it is
      // saved. The food checkout saves it; this page did not, so gift card buyers never saw
      // those menu items no matter how often they verified.
      if (data['device_token']) {
        try {
          localStorage.setItem(DEVICE_TOKEN_KEY, data['device_token']);
        } catch (e) {
          // storage blocked: the guest verifies again next time, nothing breaks
        }
      }
      this.analyticsService.identifyUser(data['phone_hash'] || null, this.getPlainPhoneNumber());
      this.offersInCart.offer_order.phone_number_verified = true;
      this.orderService.getLastPreferredPaymentForOffer(this.storeId, this.offerOrderId, this.getPlainPhoneNumber()).subscribe(data => {
        if(!data['guest_payment_detail'] && !data['is_cash'] && !data['is_store_credit']){
          this.preferredCard = null;
          this.ref.markForCheck();
          return;
        }
        if(!data['is_cash']){
          this.preferredCard = Object.assign(new CardDetails(), data['guest_payment_detail'])
        }
        this.ref.markForCheck();
      }, () => {
        this.preferredCard = null;
        this.ref.markForCheck();
      })
      this.ref.markForCheck();
    }, err => {
      // Expired, missing or rate-limited codes come back as 400/429 with a reason. Show it.
      const message = err?.error?.error || 'That code is incorrect. Please check it and try again.';
      this.snackBar.open(message, 'OK', { duration: 4000, verticalPosition: 'top' });
      this.ref.markForCheck();
    })
  }

  changePhone(){
    this.offersInCart.offer_order.phone_number_verified = false;
    this.otpSent = false;
    this.otpSending = false;
  }

  updatePhoneNumber(phoneNumber:string){
    if(this.getPlainPhoneNumberFromString(phoneNumber).length === 12 && !this.otpSent && !this.otpSending){
      this.sendOTP()
    }
  }

  changeSelectedCard(){
    this.isLoading = true;
    this.orderService.getCards(this.store.id,
      this.getPlainPhoneNumber(), this.paymentGateway).subscribe(data => {
      this.guestCardDetails = data['cards'].map(o => Object.assign(new CardDetails(), o));
      this.isLoading = false;
    },
    (error) => {
      this.guestCardDetails = []
      this.isLoading = false;
    }).add(()=>{
      this.bottomSheet.open(PaymentMethodsBottomSheetComponent, {
        panelClass: 'payment-bottom-sheet-container',
        data:  {
          isOffer: true,
          orderHash: this.offerOrderId,
          phoneNumber: this.getPlainPhoneNumber(),
          orderTotal: this.offersInCart.payment.total,
          storeCredit: null,
          cardDetails: this.guestCardDetails,
          paymentGateway: this.paymentGateway,
          storeId: this.store.id,
          storeCreditEnabled: false,
          storeCashEnabled: false,
          storeCardEnabled: this.store.has_card_payment,
          surcharge: this.store.surcharge_percent,
          payrocUsername: this.store.payroc_username,
          store: this.store
        }
      }).afterDismissed().subscribe(paymentMethod => {
        if(paymentMethod){
          this.preferredCard = paymentMethod.cardDetails
          this.paymentOptionService.updatePaymentOption(paymentMethod)
          this.loadOffer()
        }
      });
    });
  }

  getStoreId(){
    return this.offersInCart.store.id;
  }

  getPaymentGateway(){
  }

  // checkLastPreferredPayment(){
  //   if(this.getPlainPhoneNumber().length === 12){
  //     this.orderService.getPreferredPaymentForOffer(this.storeId,
  //       this.offerOrderId,
  //       this.getPlainPhoneNumber(),
  //       this.paymentGateway).subscribe(data => {
  //       if(!data['is_cash']){
  //         this.preferredCard = Object.assign(new CardDetails(), data['guest_payment_detail'])
  //       }
  //     }, () => {
  //       this.preferredCard = null;
  //     })
  //   }
  // }

  deleteCard(card:CardDetails){
    this.orderService.deleteCard(card).subscribe(() => {
      let index = this.guestCardDetails.indexOf(card, 1)
      if(index > -1){
        this.guestCardDetails.splice(index, 1)
      }
      // this.checkLastPreferredPayment()
    })
  }

  claimNow(){
    // ENG-4505: prevent double-submit — ignore if already in-flight
    if (this.isSubmitting || this.isPaymentProcessing) {
      return;
    }
    this.isSubmitting = true;

    this.orderService.submitOfferOrder(this.getStoreId(), this.offerOrderId).pipe(
      // ENG-5316: 30-second timeout — if backend hangs, switch to polling
      // instead of leaving user waiting indefinitely
      timeout(30000),
      catchError(err => {
        if (err instanceof TimeoutError) {
          // Backend hung (NMI slow) — start polling, payment may still go through
          this.isSubmitting = false;
          this.isPaymentProcessing = true;
          this.savePaymentProcessingState();
          this.ref.markForCheck();
          this.startPaymentPolling();
          return EMPTY; // ENG-5316: complete the stream silently, don't trigger error handler
        }
        return throwError(() => err);
      })
    ).subscribe(
      (response) => {
        if (response.status === 202) {
          // ENG-5090: payment still processing, start polling
          this.isSubmitting = false;
          this.isPaymentProcessing = true;
          this.savePaymentProcessingState();
          this.ref.markForCheck();
          this.startPaymentPolling();
        } else {
          this.clearPaymentProcessingState();
          this.router.navigate(['store', this.storeId,'offer', 'order', this.offerOrderId, 'status', 'success']);
        }
      },
      (err) => {
        // ENG-5316: On error, poll once to check if payment actually went
        // through before navigating to failure page
        this.isSubmitting = false;
        this.orderService.checkOfferPaymentStatus(this.getStoreId(), this.offerOrderId).subscribe(
          (result) => {
            if (result.status === 'completed') {
              this.clearPaymentProcessingState();
              this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'success']);
            } else if (result.status === 'in_progress') {
              // Payment still being processed — poll instead of showing failure
              this.isPaymentProcessing = true;
              this.savePaymentProcessingState();
              this.ref.markForCheck();
              this.startPaymentPolling();
            } else {
              this.clearPaymentProcessingState();
              this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'failure']);
            }
          },
          () => {
            this.clearPaymentProcessingState();
            this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'failure']);
          }
        );
      })
  }

  private startPaymentPolling() {
    this.pollingActive = true;
    this.savePaymentProcessingState();
    let pollCount = 0;
    const maxPolls = 72; // 72 * 5s = 6 minutes max polling time
    interval(5000).pipe(
      takeWhile(() => this.pollingActive && pollCount < maxPolls)
    ).subscribe(() => {
      pollCount++;
      this.orderService.checkOfferPaymentStatus(this.getStoreId(), this.offerOrderId).subscribe(
        (result) => {
          if (result.status === 'completed') {
            this.pollingActive = false;
            this.clearPaymentProcessingState();
            this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'success']);
          } else if (result.status === 'failed') {
            this.pollingActive = false;
            this.isPaymentProcessing = false;
            this.clearPaymentProcessingState();
            this.ref.markForCheck();
            this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'failure']);
          }
          // 'in_progress' — keep polling
        },
        () => {
          // polling error — keep trying
        }
      );
    });
  }

  ngOnDestroy() {
    this.pollingActive = false;
    if (this.paymentOptionSub) {
      this.paymentOptionSub.unsubscribe();
    }
  }

  openWallet(){
    let dialogRef = this.dialog.open(WalletComponent, {
      disableClose: true,
      data:  {order: this.offersInCart, selectMode: true, forOffer: true},
      panelClass: ['full-screen-modal']
    });
    dialogRef.afterClosed().subscribe(data => {
      // Dismissing the wallet closes it with nothing; reading cardDetail off that threw.
      if (!data?.cardDetail?.id) return;
      this.paymentOption = data;
      this.orderService.updatePreferredPaymentAsCardForOffer(this.getStoreId(), this.offerOrderId,
        data.cardDetail.id).subscribe(() => {
        this.paymentOptionService.updatePaymentOption(this.paymentOption)
      })
    })
  }
}
